/**
 * CentroCoreDataSource.ts
 *
 * Module for all initialization options for Centro
 */

import { IFloodgateHostPlatform } from '@ms-ofb/floodgate-shared-typings';
import { ConfigUpdatedListener } from "@ms/centro-hvc-loader";
import * as Configuration from "../../Configuration/Configuration";
import { AgeGroup, AuthenticationType } from "../../Configuration/Configuration";
import * as Constants from "../../Constants";
import { SDK_VERSION_PREFIX } from "../../Constants";
import { convertAutoDismissToTimeInMs, ocpsBooleanToString, ocpsEnumToString } from "../../Utils";
import Version from "../../Version";
import * as Window from "./../../Window/Window";
import { ICentroDataOptions, ICentroInitOptions, ICentroLaunchOptions } from "./CentroInterfaces";

// This is the object which is used to open/close/update data to Floodgate Centro
export class CentroCoreDataSource {
	/* Centro Init options which are not changing */
	public centroInitOptions: ICentroInitOptions;

	/* Centro Launch options which are subjective to be changed per survey triggering */
	public centroLaunchOptions: ICentroLaunchOptions;

	/* This is the list of listeners need to be invoked when the initOptions change */
	private listeners: ConfigUpdatedListener<ICentroDataOptions>[] = [];

	private static instance: CentroCoreDataSource;

	public static get() {
		if (!this.instance) {
			this.instance = new CentroCoreDataSource();
		}
		return this.instance;
	}

	constructor() {
		const complianceChecks = {
			authenticationType: AuthenticationType[(Configuration.get().getCommonInitOptions()?.authenticationType) ??
				AuthenticationType.Unauthenticated],
			ageGroup: AgeGroup[(Configuration.get().getCommonInitOptions()?.ageGroup) ?? AgeGroup.Undefined],
			policyAllowFeedback: ocpsEnumToString(Configuration.get().getInAppFeedbackInitOptions()?.sendFeedbackPolicyValue),
			policyAllowSurvey: ocpsBooleanToString(Configuration.get().getFloodgateInitOptions()?.surveyEnabled),
			policyAllowScreenshot: ocpsEnumToString(Configuration.get().getCommonInitOptions()?.screenshotPolicyValue),
			policyAllowContact: ocpsEnumToString(Configuration.get().getCommonInitOptions()?.emailPolicyValue),
		};

		this.centroInitOptions = {
			// common init options
			commonInitOptions: {
				appId: Configuration.get().getCommonInitOptions().appId,
				application: Configuration.get().getCommonInitOptions().applicationGroup,
				complianceChecks: complianceChecks,
				isProduction: Configuration.get().getCommonInitOptions().environment === Constants.Environment.Production,
				retentionDurationDays: Configuration.get().getCommonInitOptions().retentionDurationDays,
				sdkVersion: SDK_VERSION_PREFIX + (Window.get().OfficeBrowserFeedback.sdkVersion !== undefined ?
					Window.get().OfficeBrowserFeedback.sdkVersion : Version),
				telemetry: Configuration.get().getCommonInitOptions().telemetryGroup,
				web: Configuration.get().getCommonInitOptions().webGroup,
				locale: Configuration.get().getCommonInitOptions().originalLocale,
				userEmail: Configuration.get().getCommonInitOptions().userEmail,
				showEmailAddress: Configuration.get().getFloodgateInitOptions().showEmailAddress,
			},

			autoDismissValues: {
				promptAutoDismissTimeInMs: convertAutoDismissToTimeInMs(Configuration.get().getFloodgateInitOptions().autoDismiss),
			},
			hostPlatform: IFloodgateHostPlatform.Web,
		};
	}

	/*
	Register and Unregister is part of the boilerplate code to enable dynamic data passing
	*/
	public register(
		listener?: ConfigUpdatedListener<ICentroDataOptions>
	): Readonly<Partial<ICentroDataOptions>> {
		if (listener) {
			this.listeners.push(listener);
		}
		const centroDataOptions: ICentroDataOptions = {
			...this.centroInitOptions,
			...this.centroLaunchOptions
		}
		return centroDataOptions;
	}

	public unregister(listener: ConfigUpdatedListener<ICentroDataOptions>): void {
		const index = this.listeners.indexOf(listener);
		if (index > -1) {
			this.listeners.splice(index, 1);
		}
	}

	/*
	Update function to update the data source with the current survey
	 */
	public updateFloodgateSurvey(onClose: () => void) {
		// Update the CentroLaunchOptions with new survey info
		this.centroLaunchOptions = {
			uxSchema: Configuration.get().getFloodgateSurvey().getUxSchema(),
			clientFeedbackId: Configuration.get().getFloodgateSurvey().getPreGeneratedClientFeedbackId(),
			campaignId: Configuration.get().getFloodgateSurvey().getCampaignId(),
			surveyId: Configuration.get().getFloodgateSurvey().getId(),
			surveyType: Configuration.get().getFloodgateSurvey().getSurveyType(),
			isUserInitiatedSurvey: Configuration.get().getFloodgateSurvey().isUserInitiatedSurvey(),
			callbackFunctions: {
				onDismiss: (submitted: boolean) => {
					Configuration.get().getFloodgateInitOptions().onDismiss(
						Configuration.get().getFloodgateSurvey().getCampaignId(),
						submitted,
					);
					onClose();
				},
				onError: (errorMessage: string) => {
					Configuration.get().getCommonInitOptions().onError(errorMessage);
					onClose();
				}
			}
		};
		const centroDataOptions: ICentroDataOptions = {
			...this.centroInitOptions,
			...this.centroLaunchOptions
		};

		// we need to inform the existing listeners
		this.listeners.forEach((listener: ConfigUpdatedListener<ICentroDataOptions>) => listener(centroDataOptions));
	}
}
